import {
  Box,
  Typography,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  OutlinedInput,
  Radio,
} from '@mui/material';
import { KeyboardArrowDown } from '@mui/icons-material';
import { useMemo, useState } from 'react';
import { useInput } from '../../../../../../customHook/useInput';
import { useImagePreview } from '../../../../../../customHook/useProfilePicture';
import zeplinColor from '../../../../../../theme/Color';
import { validators } from '../../../../../../utilities/utilities';
import CustomButton from '../../../../../shared/CustomButton/CustomButton';
import CustomTextField from '../../../../../shared/CustomTextField/CustomTextField';
import { AdminService } from '../../../../../../service/admin.service';
import { useAppSelector } from '../../../../../../store/hooks';

type FormField = {
  label: string;
  required: boolean;
  name: string;
};

export const fields: FormField[] = [
  {
    label: 'First Name',
    required: true,
    name: 'firstName',
  },
  {
    label: 'Last Name',
    required: true,
    name: 'lastName',
  },
  {
    label: 'Email',
    required: false,
    name: 'email',
  },
];

export function UserForm({
  handleToggleDrawer,
  onAddSuccess,
  mode = 'add',
  initialData,
}: {
  handleToggleDrawer: () => void;
  onAddSuccess?: () => void;
  mode?: 'add' | 'edit';
  initialData?: {
    id?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    mobile?: string;
    roleId?: string;
    profileImage?: string;
  };
}) {
  const roles = useAppSelector((state) => state.rolesAndPermissions.filterRoles);

  const [loading, setLoading] = useState(false);
  const [selectedRole, setSelectedRole] = useState<string>(
    initialData?.roleId ?? roles[0]?.roleId ?? '',
  );

  const {
    fileInputRef,
    previewUrl,
    handleFileChange,
    clearSelection,
    openFileSelection,
    uploadProfilePicture,
  } = useImagePreview(initialData?.profileImage ?? null);

  const firstNameInput = useInput(initialData?.firstName ?? '', validators.validateName);
  const lastNameInput = useInput(initialData?.lastName ?? '', validators.validateName);
  const emailInput = useInput(initialData?.email ?? '', validators.validateEmail);
  const mobileInput = useInput(initialData?.mobile ?? '', validators.validatePhone);

  const inputHooks = {
    firstName: firstNameInput,
    lastName: lastNameInput,
    email: emailInput,
    mobile: mobileInput,
  };

  const isFormValid = fields.every((field) => {
    const fieldRequired = field.required;
    const hook = inputHooks[field.name as keyof typeof inputHooks];
    if (!fieldRequired) return true;
    if (!hook || !hook.value || hook.invalid || hook.value.trim() === '') return false;
    return true;
  });

  const handleAddUser = async () => {
    try {
      setLoading(true);

      let uploadedUrl: string | null = null;
      if (previewUrl && previewUrl !== initialData?.profileImage) {
        const result = await uploadProfilePicture({ key: false });
        uploadedUrl = result === undefined ? null : result;
      } else if (!previewUrl && initialData?.profileImage && mode === 'edit') {
        // Keep existing image if no new image selected
        uploadedUrl = initialData.profileImage;
      }

      const payload = {
        firstName: firstNameInput.value.trim(),
        lastName: lastNameInput.value.trim(),
        email: emailInput.value.trim(),
        mobile: mobileInput.value.trim(),
        roleId: selectedRole,
        profileImageUrl: uploadedUrl ?? '',
      };

      if (mode === 'edit' && initialData?.email) {
        await AdminService.updateUser(initialData.email, payload);
      } else {
        await AdminService.addUser(payload);
      }

      handleToggleDrawer();
      if (onAddSuccess) {
        onAddSuccess();
      }
    } catch (error) {
      console.error(`Failed to ${mode === 'edit' ? 'update' : 'add'} user:`, error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          height: '100vh',

          padding: { xs: '20px', xl: '24px' },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: '24px',
            width: '100%',
            flexDirection: 'column',
          }}
        >
          {/* Profile Photo Section */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: '16px',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: '80px',
                height: '80px',
                borderRadius: '22px',
                border: !previewUrl ? `1px dashed ${zeplinColor.Neutral9}` : '',
                overflow: 'hidden',
              }}
            >
              <img
                src={previewUrl ?? '/assets/account-name.svg'}
                alt='profile icon'
                width={previewUrl ? 80 : 35}
                height={previewUrl ? 80 : 35}
                style={{ objectFit: 'cover' }}
              />
            </Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                flexDirection: 'column',
                gap: '12px',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Typography variant='body2' sx={{ color: zeplinColor.BasicBlack }}>
                  Profile photo
                </Typography>
                <Typography variant='label1' sx={{ color: zeplinColor.Neutral8 }}>
                  JPG or PNG (max 2 MB)
                </Typography>
              </Box>
              <Box>
                <CustomButton
                  disabled={false}
                  onClick={openFileSelection}
                  variant='contained'
                  color={'primary'}
                  buttonVariant='buttonSmall'
                  style={{
                    borderRadius: { xs: '10px', xl: '14px' },
                    padding: '6px 12px',
                    '&.Mui-focusVisible': { backgroundColor: '#333CAA' },
                  }}
                  textStyle={{ color: zeplinColor.Neutral1 }}
                  text={previewUrl ? 'Change Photo' : 'Add Photo'}
                />
                <input
                  type='file'
                  multiple
                  accept='.jpeg, .jpg, .png, image/jpeg, image/png'
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                  onChange={handleFileChange}
                />
                {previewUrl && (
                  <CustomButton
                    disabled={false}
                    variant='contained'
                    buttonVariant='buttonSmall'
                    onClick={clearSelection}
                    text={'Delete Photo'}
                    textStyle={{
                      color: zeplinColor.Error,
                    }}
                    style={{
                      backgroundColor: 'transparent',
                      borderRadius: { xs: '10px', xl: '14px' },
                      marginLeft: '8px',
                      padding: '6px 12px',
                      '&.Mui-focusVisible, &:hover': { backgroundColor: zeplinColor.Neutral3 },
                    }}
                  />
                )}
              </Box>
            </Box>
          </Box>

          {fields.map((field) => {
            const hook = inputHooks[field.name as keyof typeof inputHooks];

            return (
              <CustomTextField
                key={field.name}
                style={{ width: '100%' }}
                onChange={hook.handleChange}
                onBlur={hook.handleBlur}
                name={field.label}
                text={hook.value}
                error={hook.invalid && hook.didChange && `Please provide a valid ${field.label}.`}
                label={field.label}
                type='text'
                tabindex={1}
                required={field.required}
                isPhoneMask={field.name === 'mobile'}
              />
            );
          })}

          <FormControl sx={{ width: '100%' }}>
            <InputLabel
              id='role-select-label'
              sx={{
                '&.Mui-focused': { color: zeplinColor.Neutral9 },
              }}
            >
              Role <span style={{ color: zeplinColor.Error }}>*</span>
            </InputLabel>
            <Select
              labelId='role-select-label'
              id='role-select'
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
              input={
                <OutlinedInput
                  sx={{
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderWidth: '1px',
                      color: zeplinColor.Neutral9,
                      borderColor: zeplinColor.Neutral9,
                    },
                  }}
                  label='Role *'
                />
              }
              renderValue={(selected) => {
                const option = roles.find((role) => role.roleId === selected);
                return option?.roleName || '';
              }}
              IconComponent={KeyboardArrowDown}
            >
              {roles.map((role) => (
                <MenuItem key={role.roleId} value={role.roleId}>
                  <Radio
                    checked={selectedRole === role.roleId}
                    sx={{
                      color: zeplinColor.BasicBlack,
                      '&.Mui-checked': {
                        color: zeplinColor.BasicBlack,
                      },
                    }}
                  />
                  {role.roleName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <CustomTextField
            style={{ width: '100%' }}
            onChange={mobileInput.handleChange}
            onBlur={mobileInput.handleBlur}
            name='Mobile'
            text={mobileInput.value}
            error={mobileInput.invalid && mobileInput.didChange && 'Please provide a valid Mobile.'}
            label='Mobile'
            type='text'
            tabindex={1}
            required={false}
            isPhoneMask={true}
          />
        </Box>
      </Box>
      <Box
        sx={{
          width: '100%',
          position: 'sticky',
          bottom: '0px',
          left: '0px',
          right: '0px',
          padding: { xs: '20px', xl: '24px' },
          margin: 'auto',
          zIndex: '100',

          backgroundColor: zeplinColor.Neutral1,
        }}
      >
        <CustomButton
          disabled={!isFormValid || loading}
          onClick={handleAddUser}
          variant='contained'
          color={loading ? 'customDisable' : 'primary'}
          style={{
            borderRadius: '14px',
            height: 56,
            width: '100%',
            '&.Mui-focusVisible': { backgroundColor: '#333CAA' },
          }}
          textStyle={{ color: loading ? zeplinColor.Neutral8 : zeplinColor.Neutral1 }}
          text={
            loading
              ? mode === 'edit'
                ? 'Updating User...'
                : 'Adding User...'
              : mode === 'edit'
                ? 'Update User'
                : 'Add User'
          }
        />
      </Box>
    </>
  );
}

export default UserForm;
